import { getApiGen } from "$root/api/factory";
import { repositoryHelperFactory } from "$root/api/repository";
import { instrumentsRepository } from "$root/functional-areas/instruments/repository";
import { workspaceRepository } from "$root/functional-areas/workspace/repository";
import { axiosExtract } from "$root/third-party-integrations/axios";
import { queryClient } from "$root/third-party-integrations/react-query/typedQueryClient";
import {
	VirtualInvestmentApiFactory,
	type CreateVirtualInvestmentRequest,
	type LabComputeResponse,
	type LabPerformance,
	type LabUpdateResponse,
	LabViewConfigApiFactory,
	type LabViewConfig,
	type UpdateVirtualInvestmentRequest,
	type VirtualInvestmentResponse,
} from "@eu.mdotm.api/portfolio-lab";
import type { ConceptDefinitionDTO, ParameterizedConceptDTO } from "@eu.mdotm.api/smart-workspaces";
import type { CompositionTemplateEntityType, CustomAttributeValue } from "@eu.mdotm.api/sphere";
import { ScenarioAnalysisControllerApiFactory } from "@eu.mdotm.api/sphere";
import { instrumentsEditorRepository } from "$root/functional-areas/instruments-editor/repository";
import { type AumCommitTarget, resolveCustomAttributesWithAum } from "$root/functional-areas/customAttributes/aum";
import { DEFAULT_LAB_AUM, editorEntityForLabType, isAumCommitCandidate, isNestedInvestment } from "./common";
import { buildExAnteConceptsConfig, type ExAnteConceptsConfig } from "./ex-ante-concepts";
import {
	labCompositionToReviewTickers,
	portfolioLabAdapter,
	type PortfolioLabComposition,
	type PortfolioLabInvestmentItem,
} from "./ctx";
import { portfolioRepository } from "../portfolio/repository";
import { referenceRepository } from "../portfolio/references-repository";
import { unreachable } from "@mdotm/utils/runtime";

const { newQuery, newAction, invalidate } = repositoryHelperFactory("portfolioLab");

const configScenarioAnalysis = () => getApiGen(ScenarioAnalysisControllerApiFactory);
const virtualInvestmentApi = () => getApiGen(VirtualInvestmentApiFactory);
const labViewConfigApi = () => getApiGen(LabViewConfigApiFactory);

/**
 * Resolves the `customAttributes` array an "Update composition" save request must carry to also
 * persist the lab's session AUM, or `undefined` for a composition-only save.
 *
 * Lab-side adapter over the shared {@link resolveCustomAttributesWithAum}: it only decides whether
 * this item is an AUM commit candidate at all (benchmarks and references never are). `target` must
 * be the entity the caller saves to, so the values are read back from the configuration the write
 * lands on.
 */
async function resolveCommitCustomAttributes(
	item: PortfolioLabInvestmentItem,
	target: AumCommitTarget,
): Promise<CustomAttributeValue[] | undefined> {
	if (!isAumCommitCandidate(item)) {
		return undefined;
	}
	return resolveCustomAttributesWithAum(item.uuid, item.aum ?? null, target);
}

// Every scalar field is sent: the PUT overwrites each one it receives, and omitting one clears it.
function virtualInvestmentUpdateBody(
	investmentCtx: PortfolioLabInvestmentItem,
	scenarioId: string | null,
): UpdateVirtualInvestmentRequest {
	const virtualInvestment = portfolioLabAdapter.virtualInvestment.toUpstream(investmentCtx);
	return {
		composition: virtualInvestment.composition,
		totalValue: virtualInvestment.aum ?? undefined,
		scenarioId: scenarioId ?? undefined,
	};
}

export type PortfolioMetricsConceptsConfig = {
	conceptDefinitions: ConceptDefinitionDTO[];
	parametrizedConcepts: ParameterizedConceptDTO[];
};

export type PortfolioLabSessionItem = {
	id: string;
	entityType: CompositionTemplateEntityType;
	uuid: string;
	currentComposition: PortfolioLabComposition[];
};

export type LabPerformanceRequest = {
	currentComposition: PortfolioLabComposition[];
	initialComposition: PortfolioLabComposition[];
	dataType: "CURRENT" | "INITIAL";
};

const NAME_CONCEPT_UUID = "5da92dc1-ddf6-4029-9f29-a5004fc621a7";
const STATUS_CONCEPT_UUID = "11edcffe-70a6-4ea9-a724-2d6d473fe3da";
// The "Portfolio" source also surfaces proposal-status portfolios (MDOT-25194). Selecting one
// loads its CURRENT snapshot (source.type "PORTFOLIO" in useInvestmentLab), which the backend
// resolves unconditionally on status. "Portfolio Proposal" (proposalList) is unchanged, so such
// portfolios appear under both options.
const INVESTMENT_LIST_STATUSES = ["READY", "ACCEPTED", "PROPOSAL_READY"];

export const portfolioLabRepository = {
	invalidate,
	investmentList: newQuery("investmentList", (search?: string) =>
		workspaceRepository.getWorkspaceData.fetchQuery({
			dataProvider: { dataProviderId: "investment", parameters: {} },
			concepts: [
				{ uuid: STATUS_CONCEPT_UUID, conceptId: "systemId: status", label: "Status", source: "CURRENT" },
				{
					uuid: NAME_CONCEPT_UUID,
					conceptId: "systemId: name",
					label: "Name",
					representationMode: "ABSOLUTE",
					source: "CURRENT",
					parameters: {},
				},
			],
			projection: [],
			sorting: [],
			limit: 1000,
			offset: 0,
			predicate: search
				? {
						type: "LOGICAL",
						keyword: "and",
						conditions: [
							{
								type: "COMPARISON",
								keyword: "in",
								parameterizedConceptUuid: STATUS_CONCEPT_UUID,
								arguments: [INVESTMENT_LIST_STATUSES] as object[],
							},
							{
								type: "COMPARISON",
								keyword: "contains",
								parameterizedConceptUuid: NAME_CONCEPT_UUID,
								arguments: [search] as unknown as object[],
							},
						],
					}
				: {
						type: "COMPARISON",
						keyword: "in",
						parameterizedConceptUuid: STATUS_CONCEPT_UUID,
						arguments: [INVESTMENT_LIST_STATUSES] as object[],
					},
		}),
	),

	proposalList: newQuery("proposalList", (search?: string) =>
		workspaceRepository.getWorkspaceData.fetchQuery({
			dataProvider: { dataProviderId: "investment", parameters: {} },
			concepts: [
				{ uuid: STATUS_CONCEPT_UUID, conceptId: "systemId: status", label: "Status", source: "CURRENT" },
				{
					uuid: NAME_CONCEPT_UUID,
					conceptId: "systemId: name",
					label: "Name",
					representationMode: "ABSOLUTE",
					source: "CURRENT",
					parameters: {},
				},
			],
			projection: [],
			sorting: [],
			limit: 1000,
			offset: 0,
			predicate: search
				? {
						type: "LOGICAL",
						keyword: "and",
						conditions: [
							{
								type: "COMPARISON",
								keyword: "eq",
								parameterizedConceptUuid: STATUS_CONCEPT_UUID,
								arguments: ["PROPOSAL_READY"] as unknown as object[],
							},
							{
								type: "COMPARISON",
								keyword: "contains",
								parameterizedConceptUuid: NAME_CONCEPT_UUID,
								arguments: [search] as unknown as object[],
							},
						],
					}
				: {
						type: "COMPARISON",
						keyword: "eq",
						parameterizedConceptUuid: STATUS_CONCEPT_UUID,
						arguments: ["PROPOSAL_READY"] as unknown as object[],
					},
		}),
	),

	referenceAndBenchmarkList: newQuery("referenceAndBenchmarkList", (search?: string) =>
		workspaceRepository.getWorkspaceData.fetchQuery({
			dataProvider: { dataProviderId: "reference", parameters: {} },
			concepts: [
				{
					uuid: NAME_CONCEPT_UUID,
					conceptId: "systemId: name",
					label: "Name",
					representationMode: "ABSOLUTE",
					source: "CURRENT",
					parameters: {},
				},
			],
			projection: [],
			sorting: [],
			limit: 1000,
			offset: 0,
			...(search
				? {
						predicate: {
							type: "COMPARISON" as const,
							keyword: "contains",
							parameterizedConceptUuid: NAME_CONCEPT_UUID,
							arguments: [search] as unknown as object[],
						},
					}
				: {}),
		}),
	),

	getInvestmentList: newQuery("getInvestmentList", () => Promise.resolve({ items: [] as PortfolioLabSessionItem[] })),

	getInvestmentComposition: newQuery("getInvestmentComposition", (_uuid: string) =>
		Promise.resolve({ composition: [] as PortfolioLabComposition[] }),
	),

	/**
	 * Commits a lab investment's current composition to its REAL (persisted) investment, routed per
	 * entity type via the shared `instrumentsEditorRepository.edit`. Nested portfolios are preserved
	 * as `PORTFOLIO_MIXED` references.
	 *
	 * For investments and proposals the same save also persists the lab's session AUM through the
	 * request's `customAttributes` (see `resolveCommitCustomAttributes`) — one atomic call, so the
	 * composition and the AUM either both apply or neither does. Benchmarks/references stay
	 * composition-only.
	 */
	updateRealInvestmentComposition: newAction(async (item: PortfolioLabInvestmentItem): Promise<void> => {
		const composition = labCompositionToReviewTickers(item);
		const portfolioSavingMode = isNestedInvestment(item) ? "NEST_PORTFOLIOS" : "MIX_INSTRUMENTS";
		const entity = editorEntityForLabType(item.type);
		switch (entity) {
			case "INVESTMENT":
				await instrumentsEditorRepository.edit.investment(item.uuid, {
					form: { portfolioSavingMode },
					composition,
					customAttributes: await resolveCommitCustomAttributes(item, "INVESTMENT"),
				});
				return;
			case "INVESTMENT_ENHANCEMENT":
				await instrumentsEditorRepository.edit.investmentEnhancement(item.uuid, {
					form: { portfolioSavingMode, editPolicy: "SAVE_WITHOUT_ACCEPT" },
					composition,
					customAttributes: await resolveCommitCustomAttributes(item, "INVESTMENT_ENHANCEMENT"),
				});
				return;
			case "BENCHMARK":
				await instrumentsEditorRepository.edit.benchmark(item.uuid, { composition });
				return;
			case "TARGET_INVESTMENT":
				await instrumentsEditorRepository.edit.targetInvestment(item.uuid, { composition });
				return;
		}
	}),

	loadComposition: newQuery(
		"loadComposition",
		async (entityType: CompositionTemplateEntityType, uuid: string): Promise<PortfolioLabComposition[]> => {
			const response = await instrumentsRepository.composition.fetchQuery(entityType, uuid, false);
			return (response.composition ?? [])
				.filter((t) => t.ticker != null)
				.map((t) =>
					t.proxyOverwriteType === "PORTFOLIO_MIXED"
						? {
								type: "portfolio" as const,
								uuid: t.ticker!,
								name: t.instrument ?? "",
								weight: t.weight ?? 0,
								monetaryWeight: ((t.weight ?? 0) / 100) * DEFAULT_LAB_AUM,
								instruments: [],
							}
						: {
								type: "instrument" as const,
								tickerName: t.ticker!,
								weight: t.weight ?? 0,
								monetaryWeight: ((t.weight ?? 0) / 100) * DEFAULT_LAB_AUM,
							},
				);
		},
	),

	portfolioMetricsConceptsConfig: newQuery(
		"metricsConceptsConfig",
		async (): Promise<PortfolioMetricsConceptsConfig> => {
			const allDefinitions = await axiosExtract(
				workspaceRepository.getConceptDefinitions.fetchQuery("VIRTUAL_INVESTMENT"),
			);
			const conceptDefinitions: ConceptDefinitionDTO[] = [];
			const parametrizedConcepts: ParameterizedConceptDTO[] = [];

			const numberOfInstrumentsDef = allDefinitions?.find(
				(definition) => definition.id === "systemId: numberOfInstruments",
			);
			const historicalVarDef = allDefinitions?.find((definition) => definition.id === "systemId: historicalvar");
			if (historicalVarDef) {
				conceptDefinitions.push(historicalVarDef);
				parametrizedConcepts.push({
					uuid: crypto.randomUUID(),
					conceptId: historicalVarDef.id!,
					label: historicalVarDef.defaultLabel ?? "",
					source: "CURRENT",
					representationMode: "ABSOLUTE",
					timeHorizon: "3Y",
					confidenceLevel: "95",
					parameters: {},
				});
			}

			const trackingErrorDef = allDefinitions?.find((definition) => definition.id === "systemId: trackingerror");

			if (trackingErrorDef) {
				conceptDefinitions.push(trackingErrorDef);
				parametrizedConcepts.push({
					uuid: crypto.randomUUID(),
					conceptId: trackingErrorDef.id!,
					source: "CURRENT",
					parameters: {},
					relativeness: {
						mode: "REFERENCE",
						uuid: null as any /*FIX-ME: be */,
						name: "Compare against each portfolio’s reference",
					},
					timeHorizon: "3Y",
					label: "Tracking Error / Current / 3Y / Rel to / Portfolio's Reference",
				});
			}

			if (numberOfInstrumentsDef) {
				conceptDefinitions.push(numberOfInstrumentsDef);
				parametrizedConcepts.push({
					uuid: crypto.randomUUID(),
					conceptId: numberOfInstrumentsDef.id!,
					label: numberOfInstrumentsDef.defaultLabel ?? "",
					source: "CURRENT",
					representationMode: "ABSOLUTE",
					parameters: {},
				});
			}

			const customScoresDef = allDefinitions.filter((d) => d.id?.includes("customScoreId"));
			customScoresDef.forEach((definition) => {
				conceptDefinitions.push(definition);
				parametrizedConcepts.push({
					uuid: crypto.randomUUID(),
					conceptId: definition.id!,
					label: definition.defaultLabel!,
					representationMode: "ABSOLUTE",
					source: "CURRENT",
				});
			});

			// Accounting metrics are custom scores too — the data provider returns a number per metric, so
			// they project as numeric columns exactly like `customScoreId` concepts.
			const accountingMetricsDef = allDefinitions.filter((d) => d.id?.includes("accountingMetricId"));
			accountingMetricsDef.forEach((definition) => {
				conceptDefinitions.push(definition);
				parametrizedConcepts.push({
					uuid: crypto.randomUUID(),
					conceptId: definition.id!,
					label: definition.defaultLabel!,
					representationMode: "ABSOLUTE",
					source: "CURRENT",
				});
			});

			return { conceptDefinitions, parametrizedConcepts };
		},
	),

	portfolioMetrics: newQuery("metrics", (virtualUuid: string, concepts: ParameterizedConceptDTO[]) =>
		workspaceRepository.getWorkspaceData.fetchQuery({
			dataProvider: {
				dataProviderId: "virtual-investment",
				parameters: { uuid: virtualUuid } as any,
			},
			concepts,
			projection: concepts.map((c) => c.uuid!).filter(Boolean),
			limit: 100,
			offset: 0,
		}),
	),

	// Same shape as `portfolioMetrics`, but against the BASELINE of the virtual investment
	// (its original, pre-edit composition) — used to derive in-cell deltas in the metrics grid.
	portfolioMetricsBaseline: newQuery("metricsBaseline", (virtualUuid: string, concepts: ParameterizedConceptDTO[]) =>
		workspaceRepository.getWorkspaceData.fetchQuery({
			dataProvider: {
				dataProviderId: "virtual-investment-baseline",
				parameters: { uuid: virtualUuid } as any,
			},
			concepts,
			projection: concepts.map((c) => c.uuid!).filter(Boolean),
			limit: 100,
			offset: 0,
		}),
	),

	exAnteScenarioConceptsConfig: newQuery(
		"exAnteScenarioConceptsConfig",
		async (): Promise<ExAnteConceptsConfig> => {
			const allDefinitions = await axiosExtract(
				workspaceRepository.getConceptDefinitions.fetchQuery("VIRTUAL_INVESTMENT"),
			);
			return buildExAnteConceptsConfig(allDefinitions ?? []);
		},
		// Resolved once per session: the parametrized-concept uuids are minted with crypto.randomUUID(),
		// so a refetch would re-key every downstream metrics query (and every colId derived from them).
		{ staleTime: Infinity, cacheTime: Infinity },
	),

	// `scenarioId` is not sent to the data provider — the scenario is bound server-side by the
	// preceding `/compute` call — but it scopes the query key so switching scenarios never serves
	// another scenario's metrics from cache.
	getExAnteScenarioMetrics: newQuery(
		"getExAnteScenarioMetrics",
		(virtualUuid: string, _scenarioId: string, concepts: ParameterizedConceptDTO[]) =>
			workspaceRepository.getWorkspaceData.fetchQuery({
				dataProvider: {
					dataProviderId: "virtual-investment",
					parameters: { uuid: virtualUuid } as any,
				},
				concepts,
				projection: concepts.map((c) => c.uuid!).filter(Boolean),
				limit: 100,
				offset: 0,
			}),
	),

	// Baseline twin of `getExAnteScenarioMetrics`: same shape, but against the BASELINE of
	// the virtual investment (its original, pre-edit composition) — used to derive in-cell deltas.
	// Not `portfolioMetricsBaseline`: that key has no scenario scoping, so the ex-ante and
	// plain-metrics surfaces would poison each other's cache. As above, `scenarioId` is not sent —
	// the scenario is bound server-side by the preceding `/compute` call — but it scopes the query
	// key so switching scenarios never serves another scenario's baseline from cache.
	getExAnteScenarioMetricsBaseline: newQuery(
		"getExAnteScenarioMetricsBaseline",
		(virtualUuid: string, _scenarioId: string, concepts: ParameterizedConceptDTO[]) =>
			workspaceRepository.getWorkspaceData.fetchQuery({
				dataProvider: {
					dataProviderId: "virtual-investment-baseline",
					parameters: { uuid: virtualUuid } as any,
				},
				concepts,
				projection: concepts.map((c) => c.uuid!).filter(Boolean),
				limit: 100,
				offset: 0,
			}),
	),

	exposure: newQuery(
		"exposure",
		(virtualUuid: string, classification: { type: "RISK_MODEL" | "TAG"; label: string; uuid: string }) =>
			workspaceRepository.getWorkspaceData.fetchQuery({
				dataProvider: {
					dataProviderId: "virtual-investment",
					parameters: { uuid: virtualUuid } as any,
				},
				concepts: [
					{
						uuid: "a4dd4e90-680e-4c3a-87f1-4f6a097e2b09",
						conceptId:
							classification.type === "TAG"
								? `customTagBreakdownId: ${classification.uuid}`
								: `riskModelBreakdownId: ${classification.uuid}`,
						representationMode: "ABSOLUTE",
						source: "CURRENT",
						parameters: {},
						label: classification.label,
					},
				],
				projection: ["a4dd4e90-680e-4c3a-87f1-4f6a097e2b09"],
				limit: 100,
				offset: 0,
			}),
	),

	getVirtualInvestmentPerformance: newQuery(
		"getVirtualInvestmentPerformance",
		async (uuid: string, scenarioId?: string, baseline?: boolean): Promise<InternalPerformanceLab> => {
			const response = await axiosExtract(
				virtualInvestmentApi().getVirtualInvestmentPerformance(uuid, scenarioId, baseline),
			);
			return {
				...response,
				series: response.series?.map(([ts, value]) => [ts / 1000, value]),
			};
		},
	),

	createVirtualInvestment: newAction(async (createVirtualInvestmentPayload: CreateVirtualInvestmentRequest) => {
		const res = await axiosExtract(virtualInvestmentApi().createVirtualInvestment(createVirtualInvestmentPayload));
		// Keep the lab's bootstrap list fresh: the list is only fetched (and seeded into the lab atoms) on
		// mount, so without this a re-entry within cacheTime would rehydrate from a pre-mutation snapshot.
		invalidate(["listVirtualInvestments"]);
		return res;
	}),

	listVirtualInvestments: newQuery("listVirtualInvestments", () =>
		axiosExtract(virtualInvestmentApi().listVirtualInvestments()),
	),

	deleteVirtualInvestment: newAction(async (virtualUuid: string): Promise<void> => {
		await axiosExtract(virtualInvestmentApi().deleteVirtualInvestment(virtualUuid));
		// Invalidate after the delete resolves (callers fire this without awaiting) so the refetch reflects
		// the removal — otherwise the removed column reappears on the next lab entry from a stale cache.
		invalidate(["listVirtualInvestments"]);
	}),

	getHistoricalScenario: newQuery("getHistoricalScenario", () =>
		axiosExtract(configScenarioAnalysis().getScenarioList()),
	),

	/**
	 * Queues a recompute of a lab column's `entity.reports` from the composition stored server-side and
	 * answers `QUEUED` with a `computeId`; the outcome arrives as a `lab-compute` event carrying it. The
	 * backend stores the result only if the column's revision has not moved meanwhile, and the outcome
	 * says which revision was stored (see `reportsRevisionAfterCompute`). A caller with edits the server
	 * has not seen syncs them first, through `syncComposition`.
	 */
	computeMetrics: newAction(
		(virtualUuid: string, scenarioId: string | null = null, signal?: AbortSignal): Promise<LabComputeResponse> =>
			axiosExtract(
				virtualInvestmentApi().computeVirtualInvestment(
					virtualUuid,
					{ scenarioId: scenarioId ?? undefined },
					{ signal },
				),
			),
	),

	updateVirtualInvestment: newAction(
		(investmentCtx: PortfolioLabInvestmentItem, scenarioId: string | null = null): Promise<LabUpdateResponse> =>
			axiosExtract(
				virtualInvestmentApi().updateVirtualInvestment(
					investmentCtx.virtualUuid,
					virtualInvestmentUpdateBody(investmentCtx, scenarioId),
				),
			),
	),

	/**
	 * The full autosave PUT that also makes its result the column's server-side baseline: the Δ and
	 * Reset read from it afterwards, and the column's revision moves even when nothing else changed.
	 */
	rebaselineVirtualInvestment: newAction(
		(investmentCtx: PortfolioLabInvestmentItem, scenarioId: string | null): Promise<LabUpdateResponse> =>
			axiosExtract(
				virtualInvestmentApi().updateVirtualInvestment(investmentCtx.virtualUuid, {
					...virtualInvestmentUpdateBody(investmentCtx, scenarioId),
					rebaseline: true,
				}),
			),
	),

	/**
	 * Persists the lab's scenario onto one column, leaving its composition alone.
	 *
	 * The picker's historical targets reuse the already-computed reports (no `/compute` runs), so this
	 * is what makes the selection outlive the page for them. `totalValue` is sent because the PUT
	 * overwrites every scalar field it receives — omitting it clears the column's AUM.
	 */
	updateVirtualInvestmentScenario: newAction(
		(investmentCtx: PortfolioLabInvestmentItem, scenarioId: string | null): Promise<LabUpdateResponse> =>
			axiosExtract(
				virtualInvestmentApi().updateVirtualInvestment(investmentCtx.virtualUuid, {
					totalValue: investmentCtx.aum ?? undefined,
					scenarioId: scenarioId ?? undefined,
				}),
			),
	),

	resetVirtualInvestment: newAction(
		(virtualUuid: string): Promise<VirtualInvestmentResponse> =>
			axiosExtract(virtualInvestmentApi().resetVirtualInvestment(virtualUuid)),
	),

	// View-config is client-authoritative: seeded once into the lab's editor store on mount, after which
	// the client writes optimistically and pushes to the backend (see `updateLabSession`). `staleTime:
	// Infinity` + never self-invalidating after the PUT reproduces that "load once, client wins" behavior
	// natively — a focus / mount / reconnect refetch can no longer stomp an in-flight edit. `cacheTime`
	// stays at the repository default, so a COLD remount (after the cache expires) still refetches server
	// truth.
	getLabSession: newQuery("lab-session", () => axiosExtract(labViewConfigApi().getLabViewConfig()), {
		staleTime: Infinity,
	}),
	updateLabSession: newAction(async (labSessionPayload: LabViewConfig) => {
		await labViewConfigApi().setLabViewConfig(labSessionPayload);
		// Mirror the just-persisted config into the query cache rather than invalidating it: no refetch is
		// needed (the FE authored this value), and a WARM remount within `cacheTime` then re-seeds from
		// client truth instead of a pre-edit snapshot. On a PUT failure we throw before this line, so the
		// cache is left untouched — no reconciliation; the client keeps its edits and the next successful
		// save catches the server up (mirrors the composition-sync keep-on-error contract).
		queryClient.setQueryData(portfolioLabRepository.getLabSession.fullQueryKey, labSessionPayload);
	}),

	summary: newQuery("summary", async (uuid: string, type: PortfolioLabInvestmentItem["type"]) => {
		switch (type) {
			case "INVESTMENT":
				return portfolioRepository.summary.fetchQuery(uuid);
			case "INVESTMENT_PROPOSAL":
				return portfolioRepository.summary.fetchQuery(uuid, { enhanced: true });
			case "BENCHMARK":
				return referenceRepository.benchmarkSummary.fetchQuery(uuid);
			case "REFERENCE":
				return portfolioRepository.summary.fetchQuery(uuid);
			default:
				unreachable();
		}
	}),
};

export type InternalPerformanceLab = Omit<LabPerformance, "series"> & {
	series?: Array<[timestampSec: number, value: number]>;
};
